import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Shield, Mail, Phone } from "lucide-react";
import Navigation from "../components/layout/Navigation";
import Footer from "../components/layout/Footer";
import Breadcrumb from "../components/common/Breadcrumb";

const PolitiqueConfidentialitePage = () => {
  useEffect(() => {
    document.title = "Politique de Confidentialité | Sylva Invest";
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', "Politique de confidentialité de Sylva Invest. Informations sur la collecte, l'utilisation et la protection de vos données personnelles conformément au RGPD.");
    }
    let canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.setAttribute('href', 'https://sylva-invest.com/politique-confidentialite');
    return () => {
      document.title = "Sylva Invest — Transactions Forestières, Remembrement & Conseil (France)";
      if (metaDescription) metaDescription.setAttribute('content', 'Sylva Invest accompagne propriétaires, héritiers et investisseurs pour recomposer le foncier forestier morcelé.');
      if (canonical) canonical.setAttribute('href', 'https://sylva-invest.com/');
    };
  }, []);

  const sections = [
    {
      title: "1. Responsable du traitement",
      content: `Sylva Invest, société spécialisée dans les transactions forestières et le remembrement foncier, est responsable du traitement des données personnelles collectées via ce site.

Contact : contact@sylva-invest.com
Téléphone : +33 6 81 48 12 37`
    },
    {
      title: "2. Données collectées",
      content: `Dans le cadre de nos formulaires de contact et de demande d'estimation, nous collectons les données suivantes :

• Données d'identification : nom, prénom
• Coordonnées : adresse email, numéro de téléphone
• Données relatives à votre propriété forestière : commune, département, surface approximative, références cadastrales (facultatif), essences présentes
• Données relatives à votre situation : type de propriété, objectif, délai envisagé
• Message libre (facultatif)

Ces données sont nécessaires pour traiter votre demande et vous recontacter.`
    },
    {
      title: "3. Finalités du traitement",
      content: `Vos données personnelles sont collectées et traitées pour les finalités suivantes :

• Répondre à vos demandes de contact et d'information
• Analyser votre situation et vous proposer un accompagnement adapté
• Vous recontacter dans le cadre de votre projet forestier
• Établir des statistiques anonymisées sur l'utilisation du site

Vos données ne sont jamais vendues à des tiers.`
    },
    {
      title: "4. Base légale du traitement",
      content: `Le traitement de vos données repose sur :

• Votre consentement explicite, recueilli via la case à cocher du formulaire
• L'exécution de mesures précontractuelles à votre demande
• Notre intérêt légitime à développer notre activité commerciale`
    },
    {
      title: "5. Destinataires des données",
      content: `Vos données sont destinées uniquement à l'équipe Sylva Invest chargée de traiter votre demande.

Elles peuvent être transmises à nos sous-traitants techniques (hébergement, envoi d'emails) dans le strict respect du RGPD.

Nous n'effectuons aucun transfert de données hors de l'Union Européenne.`
    },
    {
      title: "6. Durée de conservation",
      content: `Vos données sont conservées pendant une durée maximale de 3 ans à compter de votre dernière interaction avec Sylva Invest.

Passé ce délai, vos données sont supprimées ou anonymisées.

Si une relation commerciale est établie, les données nécessaires à la gestion de cette relation sont conservées conformément aux obligations légales (durée de prescription).`
    },
    {
      title: "7. Vos droits",
      content: `Conformément au Règlement Général sur la Protection des Données (RGPD), vous disposez des droits suivants :

• Droit d'accès : obtenir la confirmation que des données vous concernant sont traitées et en recevoir une copie
• Droit de rectification : demander la correction de données inexactes
• Droit à l'effacement : demander la suppression de vos données
• Droit à la limitation : demander la suspension du traitement
• Droit à la portabilité : recevoir vos données dans un format structuré
• Droit d'opposition : vous opposer au traitement pour des raisons légitimes
• Droit de retirer votre consentement à tout moment

Pour exercer ces droits, contactez-nous à : contact@sylva-invest.com`
    },
    {
      title: "8. Cookies et traceurs",
      content: `Ce site utilise Google Analytics et Google Ads pour mesurer l'audience et l'efficacité de nos campagnes publicitaires.

Ces outils déposent des cookies permettant de :
• Mesurer le nombre de visiteurs et les pages consultées
• Analyser la provenance du trafic (UTM, gclid)
• Suivre les conversions (formulaires envoyés)

Aucune donnée personnelle (nom, email, téléphone, adresse) n'est transmise à Google. Seules des données anonymisées ou pseudonymisées sont collectées.

Vous pouvez configurer votre navigateur pour refuser les cookies ou installer une extension de blocage.`
    },
    {
      title: "9. Sécurité des données",
      content: `Nous mettons en œuvre des mesures techniques et organisationnelles appropriées pour protéger vos données contre tout accès non autorisé, modification, divulgation ou destruction.

• Transmission sécurisée via HTTPS
• Accès restreint aux personnes habilitées
• Sous-traitants conformes au RGPD`
    },
    {
      title: "10. Réclamation",
      content: `Si vous estimez que le traitement de vos données ne respecte pas la réglementation, vous pouvez introduire une réclamation auprès de la CNIL :

Commission Nationale de l'Informatique et des Libertés
3 Place de Fontenoy, TSA 80715
75334 Paris Cedex 07
www.cnil.fr`
    },
    {
      title: "11. Modification de la politique",
      content: `Cette politique de confidentialité peut être mise à jour à tout moment. La date de dernière mise à jour est indiquée ci-dessous.

Dernière mise à jour : Décembre 2024`
    }
  ];

  return (
    <>
      <Navigation />
      <main className="pt-20">
        {/* Hero */}
        <section className="bg-forest text-white py-12 md:py-16">
          <div className="max-w-4xl mx-auto px-6 md:px-12">
            <Breadcrumb items={[{ label: "Politique de confidentialité" }]} />
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="flex items-center mb-6">
                <Shield className="w-10 h-10 text-gold mr-4" />
                <h1 className="font-serif text-2xl md:text-3xl lg:text-4xl font-normal tracking-tight" data-testid="page-h1">
                  Politique de Confidentialité
                </h1>
              </div>
              <p className="font-sans text-base text-white/80 leading-relaxed">
                Sylva Invest s'engage à protéger vos données personnelles conformément au Règlement Général sur la Protection des Données (RGPD).
              </p>
            </motion.div>
          </div>
        </section>

        {/* Content */}
        <section className="py-12 md:py-16 bg-cream">
          <div className="max-w-4xl mx-auto px-6 md:px-12">
            <div className="space-y-10">
              {sections.map((section, index) => (
                <motion.div
                  key={section.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.05 }}
                  className="bg-white p-6 md:p-8 border border-gray-100"
                >
                  <h2 className="font-serif text-lg md:text-xl text-forest mb-4">
                    {section.title}
                  </h2>
                  <div className="font-sans text-sm md:text-base text-gray-700 leading-relaxed whitespace-pre-line">
                    {section.content}
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Contact CTA */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="mt-12 bg-bone p-6 md:p-8 border border-gray-200"
            >
              <h3 className="font-serif text-xl text-forest mb-4">
                Une question sur vos données ?
              </h3>
              <p className="font-sans text-gray-600 mb-6">
                Pour toute question relative à la protection de vos données personnelles, n'hésitez pas à nous contacter.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a
                  href="mailto:contact@sylva-invest.com"
                  className="inline-flex items-center font-sans text-sm font-medium text-forest hover:text-gold transition-colors"
                >
                  <Mail className="w-4 h-4 mr-2" />
                  contact@sylva-invest.com
                </a>
                <a
                  href="tel:+33681481237"
                  className="inline-flex items-center font-sans text-sm font-medium text-forest hover:text-gold transition-colors"
                >
                  <Phone className="w-4 h-4 mr-2" />
                  +33 6 81 48 12 37
                </a>
              </div>
            </motion.div>

            {/* Back link */}
            <div className="mt-8">
              <Link
                to="/"
                className="inline-flex items-center font-sans text-sm font-medium text-forest hover:text-gold transition-colors"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Retour à l'accueil
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
};

export default PolitiqueConfidentialitePage;
